import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import { getCurrentTimestamp } from "../../../../Helper/DatePicker/DateConverter";
import FullScreenDialog from "../../../../Components/FullScreenDialog";
import CloseButton from "../../../../Components/CloseButton";
import GenericDraggableTableView from "../../../../Features/Table/GenericDraggableTable";
import { getColumnsConfig } from "../../../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations";
import { encryptTheParams } from "../../../../Helper/QueryParams/EncryptDecrypt";
import { getWarrantyDrillDownData, getSOHDetailsTableAction } from "../../../../Api/BatteryHealthApi";
import { usePagesContent, useBaseEntityInfo } from "../../../../Hooks/usePagesContent";
import CODE from "../../../../Static/Constants/StatusCodes";
import {
  SOH_BREAKDOWN_COLUMNS_CONFIG,
  SOH_BREAKDOWN_COLUMN_ORDER,
  getSohBreakdownCustomRenderers,
} from "../SOHBreakdown/SOHBreakdownTableConfig";
import { getRulCustomRenderers } from "../RULDetails/RULDetailsTableConfig";
import { flattenRow } from "../utils/tableUtils";

const PAGE_SIZE = 20;

// ─── Default static fallback (used when org meta has no RULVsWarrantyDrillDown) ─
const DEFAULT_SELECT   = ["RULCyclesPredicted", "RULKmPredicted", "warrantyRemainingMonths", "warrantyRemainingCycles", "warrantyRemainingKm"];
const DEFAULT_METAINFO = ["licensePlateNo", "pingStatus"];

// ─── Component ────────────────────────────────────────────────────────────────
const RULWarrantyDrillDownModal = ({ open, onClose, selectedDataPoint, configName, globalFilters = {} }) => {
  const pagesContent = usePagesContent();
  const { baseEntityTypePluralLable } = useBaseEntityInfo();

  const onBatteryIDClick = useCallback((record) => {
    const hash = encryptTheParams({ batteryID: record.batteryID, tabValue: "health" });
    window.open(`/specificBattery?${hash}`, "_blank", "noreferrer");
  }, []);

  const customRenderers = useMemo(
    () => ({
      ...getSohBreakdownCustomRenderers(onBatteryIDClick),
      ...getRulCustomRenderers(onBatteryIDClick),
    }),
    [onBatteryIDClick]
  );

  // ── Org-meta derived schema ───────────────────────────────────────────────────
  const [orgMetaSelect,   setOrgMetaSelect]   = useState(DEFAULT_SELECT);
  const [orgMetaMetaInfo, setOrgMetaMetaInfo] = useState(DEFAULT_METAINFO);
  const [schemaReady,     setSchemaReady]     = useState(false);

  // ── Table state ───────────────────────────────────────────────────────────────
  const [tableData,            setTableData]            = useState([]);
  const [total,                setTotal]                = useState(0);
  const [isLoading,            setIsLoading]            = useState(false);
  const [pageNumber,           setPageNumber]           = useState(1);
  const [sortBy,               setSortBy]               = useState([]);
  const [tableColumns,         setTableColumns]         = useState([]);
  const [selectedColumnKeys,   setSelectedColumnKeys]   = useState([]);
  const [initialColumns,       setInitialColumns]       = useState([]);
  const [defaultSelectedKeys,  setDefaultSelectedKeys]  = useState([]);
  const [columnOrder,          setColumnOrder]          = useState([]);
  const [totalSelectedRowKeys, setTotalSelectedRowKeys] = useState(new Set());
  const [isSelectAll,          setIsSelectAll]          = useState(false);

  // ── Helper: initialise column state ─────────────────────────────────────────
  const initColumnsFromConfig = useCallback((config, order) => {
    const cols = getColumnsConfig({ tableColumns: config, tableColumnOrder: order, customRenderers });
    setInitialColumns(cols);
    setTableColumns(cols);
    const keys = cols.map((c) => c.dataIndex);
    setSelectedColumnKeys(keys);
    setDefaultSelectedKeys(keys);
    setColumnOrder(cols.map((c) => c.headerLabel));
  }, [customRenderers]);

  // ── Step 1: Read schema from org meta ────────────────────────────────────────
  useEffect(() => {
    const tableSchema = pagesContent?.metaInfo?.BatteryIntelligence?.RULVsWarrantyDrillDown?.table;
    if (tableSchema) {
      setOrgMetaSelect(tableSchema.select   || DEFAULT_SELECT);
      setOrgMetaMetaInfo(tableSchema.metaInfo || DEFAULT_METAINFO);
      initColumnsFromConfig(tableSchema.columnsConfig, tableSchema.columnOrdering || []);
    } else {
      // Fallback to SOH breakdown static config
      initColumnsFromConfig(SOH_BREAKDOWN_COLUMNS_CONFIG, SOH_BREAKDOWN_COLUMN_ORDER);
    }
    setSchemaReady(true);
  }, [pagesContent, initColumnsFromConfig]);

  // ── Reset when modal closes ───────────────────────────────────────────────────
  useEffect(() => {
    if (!open) {
      setTableData([]);
      setTotal(0);
      setPageNumber(1);
      setSortBy([]);
      setTotalSelectedRowKeys(new Set());
      setIsSelectAll(false);
    }
  }, [open]);

  // ── Reset pagination when data point changes ─────────────────────────────────
  useEffect(() => {
    if (open && selectedDataPoint) {
      setPageNumber(1);
      setSortBy([]);
    }
  }, [open, selectedDataPoint]);

  // ── Step 2: Fetch from API ────────────────────────────────────────────────────
  useEffect(() => {
    if (!open || !selectedDataPoint || !configName || !schemaReady) return;
    let active = true;
    const fetch = async () => {
      setIsLoading(true);
      try {
        const result = await getWarrantyDrillDownData(
          configName,
          selectedDataPoint.warrantyBucket ?? selectedDataPoint.cardTitle,
          pageNumber,
          PAGE_SIZE,
          sortBy,
          globalFilters,
          orgMetaSelect,
          orgMetaMetaInfo
        );
        if (!active) return;
        const rows = result?.responseStatus?.code === CODE.SUCCESS
          ? (result?.response?.data || []).map(flattenRow)
          : [];
        setTableData(rows);
        setTotal(result?.response?.paginationInfo?.total || 0);
      } catch (err) {
        console.error("RULWarrantyDrillDown: fetch error", err);
        if (!active) return;
        setTableData([]);
        setTotal(0);
      } finally {
        if (active) setIsLoading(false);
      }
    };
    fetch();
    return () => { active = false; };
  }, [open, selectedDataPoint, configName, pageNumber, sortBy, globalFilters,
      schemaReady, orgMetaSelect, orgMetaMetaInfo]);

  const handlePageChange = (page) => {
    setTotalSelectedRowKeys(new Set());
    setIsSelectAll(false);
    setPageNumber(page);
  };

  const handleSortChange = (key, order) => {
    setSortBy([{ column: key, direction: order === "asc" ? "ASC" : "DESC" }]);
    setPageNumber(1);
    setTotalSelectedRowKeys(new Set());
    setIsSelectAll(false);
  };

  const getCurrentSorting = () => {
    if (sortBy.length > 0) return { sortBy: sortBy[0].column, orderBy: sortBy[0].direction.toLowerCase() };
    return {};
  };

  const applySelectedColumns = (newKeys) => {
    const filtered = initialColumns.filter((c) => newKeys.includes(c.dataIndex));
    setTableColumns(filtered);
    setSelectedColumnKeys(newKeys);
    setColumnOrder(filtered.map((c) => c.headerLabel));
  };

  const onColumnOrderChange = (newCols) => {
    setTableColumns(newCols);
    setColumnOrder(newCols.map((c) => c.headerLabel));
  };

  const onSelectChange = (record, selected) => {
    const updated = new Set(totalSelectedRowKeys);
    if (selected) updated.add(record.id);
    else {
      updated.delete(record.id);
      if (isSelectAll) setIsSelectAll(false);
    }
    setTotalSelectedRowKeys(updated);
  };

  const onSelectAllChange = (selected, selectedRows) => {
    setIsSelectAll(selected);
    setTotalSelectedRowKeys(
      selected ? new Set(selectedRows.map((r) => r?.id)) : new Set()
    );
  };

  const buildActionBody = useCallback((actionType, emailParams = {}) => ({
    actionType,
    groupBy: ["batteryID"],
    metaInfo: orgMetaMetaInfo,
    select: orgMetaSelect,
    filters: [
      { name: "configName", value: [configName] },
      ...(selectedDataPoint ? [{ name: "WarrantyBucket", value: [selectedDataPoint.warrantyBucket ?? selectedDataPoint.cardTitle] }] : []),
    ],
    columns: tableColumns.map((c) => ({ dataIndex: c.dataIndex, headerLabel: c.headerLabel })),
    columnOrder: tableColumns.map((c) => c.dataIndex),
    selectAll: isSelectAll,
    resourceIDs: Array.from(totalSelectedRowKeys),
    sortBy: sortBy[0]?.column || "",
    orderBy: sortBy[0]?.direction || "ASC",
    fileName: `Warranty_DrillDown_BatteryList_${getCurrentTimestamp("YYYY-MM-DD")}.csv`,
    ...emailParams,
  }), [orgMetaMetaInfo, orgMetaSelect, configName, selectedDataPoint, tableColumns, isSelectAll, totalSelectedRowKeys, sortBy]);

  const getCSVData = useCallback(async () => {
    const res = await getSOHDetailsTableAction(globalFilters, buildActionBody("download"));
    return res?.response?.data ?? [];
  }, [globalFilters, buildActionBody]);

  const onSendEmail = useCallback(async ({ sendTo, sendCc, subject, body }) => {
    await getSOHDetailsTableAction(globalFilters, buildActionBody("mail", {
      emailTo: sendTo,
      emailCc: sendCc,
      subject,
      body,
    }));
  }, [globalFilters, buildActionBody]);

  const getCsvColumnSettings = useCallback(() => ({
    columnOrder: tableColumns.map((c) => c.headerLabel),
  }), [tableColumns]);

  const titlePath = useMemo(() => {
    if (!selectedDataPoint) return "";
    return `RUL vs. Warranty / ${selectedDataPoint.cardTitle || ""}`;
  }, [selectedDataPoint]);

  return (
    <FullScreenDialog
      open={open}
      handleClose={onClose}
      containerStyles={{ bgcolor: "#FAFAFA", overflowY: "hidden" }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden", bgcolor: "#FAFAFA" }}>

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            borderBottom: "1px solid #E6E6E6",
            px: 3,
            py: 2,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography variant="fw700s20c000000" sx={{ lineHeight: "28px" }}>
              Drill Down:
            </Typography>
            <Typography variant="fw500s16c8F8F8F" sx={{ lineHeight: "28px" }}>
              {titlePath}
            </Typography>
          </Box>
          <CloseButton onClick={onClose} />
        </Box>

        {/* ── Table ───────────────────────────────────────────────────────── */}
        <Box sx={{ flex: 1, overflow: "auto", px: 3, py: 2, minHeight: 0 }}>
          <GenericDraggableTableView
            title={`${baseEntityTypePluralLable} List`}
            showPrimaryButton={false}
            count={total || tableData.length}
            tableColumns={tableColumns}
            selectedColumns={selectedColumnKeys}
            tableData={tableData}
            availableEmailDomains={pagesContent?.metaInfo?.availableEmailDomains || []}
            initialColumns={initialColumns}
            applySelectedColumns={applySelectedColumns}
            defaultSelectedColumns={defaultSelectedKeys}
            isLoading={isLoading}
            disablePagination={false}
            pageNumber={pageNumber}
            rowsPerPage={PAGE_SIZE}
            total={total}
            disableSearch={true}
            disableFiltersSettings={true}
            disableColumnSettings={false}
            columnOrder={columnOrder}
            onColumnOrderChange={onColumnOrderChange}
            applySort={handleSortChange}
            initialSorting={getCurrentSorting()}
            emptyMessage={`No ${baseEntityTypePluralLable.toLowerCase()} found for the selected warranty category`}
            containerStyles={{ backgroundColor: "#FFFFFF", borderRadius: "8px", border: "1px solid #E6E6E6" }}
            getTotalRowSelection={() => Array.from(totalSelectedRowKeys)}
            getTotalRowSelectionCount={() =>
              isSelectAll ? (total || tableData.length) : totalSelectedRowKeys.size
            }
            rowSelection={{
              selectedRowKeys: Array.from(totalSelectedRowKeys),
              onSelect: onSelectChange,
              onSelectAll: onSelectAllChange,
            }}
            onClearSelection={() => { setTotalSelectedRowKeys(new Set()); setIsSelectAll(false); }}
            onPageChange={handlePageChange}
            getCSVData={getCSVData}
            onSendEmail={onSendEmail}
            getCsvColumnSettings={getCsvColumnSettings}
          />
        </Box>

      </Box>
    </FullScreenDialog>
  );
};

export default RULWarrantyDrillDownModal;
